// Pay periods get locked, unlocked and relocked many times. The History view shows one card per
// pay period (its latest log) and opens the full chronology on demand.

export const PAY_PERIOD_LOG_TYPES = ["lock-pay-group", "unlock-pay-group", "pay-period-calc"];

const calendarDay = (value) => (typeof value === "string" ? value.slice(0, 10) : "");

// The pay period a log belongs to, or null when it is not a pay period log (or has no dates).
// Calendar days only (first 10 chars), so a timezone suffix never splits a group.
export const payPeriodKey = (log) => {
    if (!log || !PAY_PERIOD_LOG_TYPES.includes(log.type)) return null;
    const start = calendarDay(log.payGroupItemStartDate);
    const end = calendarDay(log.payGroupItemEndDate);
    if (!start || !end) return null;
    return [log.payGroup || "", start, end].join("|");
};

// When the log was written, in seconds: timestamp, then effectiveTimestamp, then date.
const toSeconds = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number) || number <= 0) return null;
    return number > 1e11 ? number / 1000 : number; // milliseconds -> seconds
};

const parseDate = (value) => {
    if (!value) return null;
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms / 1000 : null;
};

export const logTime = (log) =>
    toSeconds(log?.timestamp) ?? toSeconds(log?.effectiveTimestamp) ?? parseDate(log?.date) ?? 0;

// Oldest first. Ties: the write date (ms precision) and then the position in the list.
const compareEntries = (a, b) =>
    a.time - b.time || (parseDate(a.log?.date) ?? 0) - (parseDate(b.log?.date) ?? 0) || a.index - b.index;

// logs -> [{ log, key, group }] in the original order.
// A pay period log appears once per pay period: its latest log, at that log's position, with
// `group` holding every log of the period oldest first. Other logs pass through with group = null.
export const groupPayPeriodLogs = (logs) => {
    if (!Array.isArray(logs)) return [];

    const groups = new Map();
    logs.forEach((log, index) => {
        const key = payPeriodKey(log);
        if (!key) return;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({ log, index, time: logTime(log) });
    });

    const latestIndex = new Map();
    const chronology = new Map();
    groups.forEach((entries, key) => {
        const sorted = [...entries].sort(compareEntries);
        chronology.set(key, sorted.map((entry) => entry.log));
        latestIndex.set(key, sorted[sorted.length - 1].index);
    });

    const result = [];
    logs.forEach((log, index) => {
        const key = payPeriodKey(log);
        if (!key) {
            result.push({ log, key: null, group: null });
        } else if (latestIndex.get(key) === index) {
            result.push({ log, key, group: chronology.get(key) });
        }
    });
    return result;
};

const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

// Booked balance of a bucket: initial + earned + updates - consumed (null when the log has none).
export const bookedBalance = (bucket) => {
    if (!bucket || typeof bucket !== "object") return null;
    return toNumber(bucket.initial) + toNumber(bucket.earned) + toNumber(bucket.updates) - toNumber(bucket.consumed);
};

// The net effect of one pay period, from its logs (oldest first, as groupPayPeriodLogs gives them).
// Every lock books the whole period again (an unlock reverses it first), so while the period is
// locked its effect is its latest lock; an unlocked period has no effect until it is locked again.
//   accrued / used / lost: hours (used and lost positive, the caller signs them)
//   balance: booked balance after the latest log, or null
//   locks / unlocks: how many times it was locked / unlocked; relocks = locks - 1
//   lastLock: the latest lock log, latest: the latest log of any kind
export const summarizePayPeriod = (group) => {
    const logs = Array.isArray(group) ? group.filter(Boolean) : [];
    const latest = logs[logs.length - 1] || null;
    const locks = logs.filter((log) => log.type !== "unlock-pay-group");
    const lastLock = locks[locks.length - 1] || null;
    const unlocks = logs.length - locks.length;
    const locked = !!latest && latest.type !== "unlock-pay-group";
    const diff = (locked && latest.diff) || {};
    return {
        latest,
        lastLock,
        locked,
        accrued: toNumber(diff.earned),
        used: toNumber(diff.consumed),
        lost: toNumber(diff.lost),
        balance: bookedBalance(latest?.new),
        locks: locks.length,
        unlocks,
        relocks: Math.max(locks.length - 1, 0),
    };
};
